/**
 * Alertes de la paroisse déjà vues dans le centre de notifications.
 *
 * Tournage des vidéos de formation des curés (11/10/2026) : le point rouge du
 * menu affichait « 2 » (les alertes rouges du jour) quand l'onglet du centre
 * disait « Non lues (0) », et ouvrir le centre ne l'éteignait pas. Le point
 * rouge compte désormais les notifications non lues PLUS les alertes rouges
 * pas encore vues ; ouvrir le centre marque vues les alertes qu'il affiche.
 *
 * Ce qui est vu se retient dans le navigateur, par compte, par paroisse et par
 * jour : le lendemain, tout se rallume. Une alerte se reconnaît à son code ET
 * à ses paramètres : une alerte qui change (« 3 dons à valider » devenus 4)
 * rallume le point, comme une alerte nouvelle. Chaque lecture et écriture du
 * stockage est protégée : sans stockage (navigation privée, stockage bloqué),
 * ce qui a été vu tient en mémoire le temps de la session, et l'écran marche.
 */
import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { Alerte } from "@/pages/dashboard-parts/alertes";
import { toIsoDay } from "@/pages/dashboard-parts/periodes";

const PREFIXE = "catho_paroisses_alertes_vues";

interface Etat {
  jour: string;
  vues: string[];
}

/** Repli quand l'écriture dans le stockage du navigateur échoue. */
const memoire = new Map<string, Etat>();
const abonnes = new Set<() => void>();

function prevenir(): void {
  abonnes.forEach((f) => f());
}

/** Code de l'alerte et ses paramètres : ce qui la distingue d'une autre. */
export function signatureAlerte(a: Pick<Alerte, "code" | "params">): string {
  const params = Object.keys(a.params)
    .sort()
    .map((k) => `${k}=${String(a.params[k])}`)
    .join("&");
  return params ? `${a.code}?${params}` : a.code;
}

function cleStockage(compte: string, paroisse: string): string {
  return `${PREFIXE}:${compte}:${paroisse}`;
}

function lireStockage(cle: string): Etat | null {
  try {
    const brut = localStorage.getItem(cle);
    if (!brut) return null;
    const etat = JSON.parse(brut) as Partial<Etat> | null;
    if (etat && typeof etat.jour === "string" && Array.isArray(etat.vues)) {
      return { jour: etat.jour, vues: etat.vues.filter((v): v is string => typeof v === "string") };
    }
  } catch {
    /* stockage indisponible ou valeur illisible : rien de vu */
  }
  return null;
}

/** Signatures des alertes vues ce jour-là par ce compte, pour cette paroisse. */
export function lireAlertesVues(compte: string, paroisse: string, jour: string): string[] {
  const cle = cleStockage(compte, paroisse);
  const vues = new Set<string>();
  for (const etat of [lireStockage(cle), memoire.get(cle)]) {
    if (etat?.jour === jour) etat.vues.forEach((v) => vues.add(v));
  }
  return Array.from(vues);
}

/** Retient ces alertes comme vues ; celles d'un autre jour sont oubliées. */
export function marquerAlertesVues(
  compte: string,
  paroisse: string,
  jour: string,
  signatures: string[],
): void {
  const deja = lireAlertesVues(compte, paroisse, jour);
  const nouvelles = signatures.filter((s) => !deja.includes(s));
  if (nouvelles.length === 0) return;
  const cle = cleStockage(compte, paroisse);
  const etat: Etat = { jour, vues: [...deja, ...nouvelles] };
  try {
    localStorage.setItem(cle, JSON.stringify(etat));
  } catch {
    memoire.set(cle, etat);
  }
  prevenir();
}

/** Pour les tests : oublie ce qui n'a tenu qu'en mémoire. */
export function oublierAlertesVues(): void {
  memoire.clear();
}

function abonner(rappel: () => void): () => void {
  abonnes.add(rappel);
  // Un autre onglet du même navigateur a ouvert le centre de notifications.
  const surStockage = (e: StorageEvent) => {
    if (!e.key || e.key.startsWith(PREFIXE)) rappel();
  };
  window.addEventListener("storage", surStockage);
  return () => {
    abonnes.delete(rappel);
    window.removeEventListener("storage", surStockage);
  };
}

const SEPARATEUR = "\n";

/**
 * Alertes vues par ce compte aujourd'hui, pour cette paroisse, et le geste
 * qui en marque de nouvelles. Sans compte ni paroisse : rien de vu, rien à
 * retenir.
 */
export function useAlertesVues(
  compte: string | null | undefined,
  paroisse: string | null | undefined,
): { vues: ReadonlySet<string>; marquerVues: (signatures: string[]) => void } {
  // Instantané en chaîne : deux lectures identiques sont égales, React ne
  // redessine que si ce qui est vu change.
  const instantane = useSyncExternalStore(
    abonner,
    () =>
      compte && paroisse
        ? lireAlertesVues(compte, paroisse, toIsoDay(new Date())).join(SEPARATEUR)
        : "",
    () => "",
  );
  const vues = useMemo(
    () => new Set(instantane ? instantane.split(SEPARATEUR) : []),
    [instantane],
  );
  const marquerVues = useCallback(
    (signatures: string[]) => {
      if (!compte || !paroisse) return;
      marquerAlertesVues(compte, paroisse, toIsoDay(new Date()), signatures);
    },
    [compte, paroisse],
  );
  return { vues, marquerVues };
}
